"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Redactor = void 0;
const lodash_1 = require("lodash");
/**
 * Redactors define when and how redaction should occur
 * They are primarily used with the `Sanitizer`
 */
class Redactor {
    // =============================================================================
    // Class functions
    // =============================================================================
    constructor(keyRegex, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    redactFn) {
        this.keyRegex = keyRegex;
        this.redactFn = redactFn;
    }
    matchKey(key) {
        var _a;
        if (key.length > Redactor.KEY_MAX_LENGTH) {
            return false;
        }
        return (_a = this.keyRegex) === null || _a === void 0 ? void 0 : _a.some((regex) => regex.test(key));
    }
    redactValue(value) {
        try {
            if (this.redactFn) {
                return (0, lodash_1.isObjectLike)(value) ? this.redactFn(value) : this.redactFn(value === null || value === void 0 ? void 0 : value.toString());
            }
            return Redactor.DEFAULT_REDACTED_TEXT;
        }
        catch (error) {
            return Redactor.DEFAULT_REDACTED_TEXT;
        }
    }
    // =============================================================================
    // Static functions
    // =============================================================================
    /**
     * Registers a redactor to be checked every time `redactObject` is called
     * WARNING: There are performance costs added with every registered redactor
     */
    static registerRedactor(redactor) {
        Redactor.registeredRedactors.unshift(redactor);
        return redactor;
    }
    /**
     * Find the redactor that matches this key
     */
    static matchKey(key, customRedactors = []) {
        for (const redactorOrString of customRedactors) {
            if (redactorOrString instanceof Redactor) {
                if (redactorOrString.matchKey(key)) {
                    return redactorOrString;
                }
            }
            else if (key === redactorOrString) {
                return new Redactor([]);
            }
        }
        for (const redactor of Redactor.registeredRedactors) {
            if (redactor.matchKey(key)) {
                return redactor;
            }
        }
    }
}
exports.Redactor = Redactor;
Redactor.registeredRedactors = new Array();
Redactor.DEFAULT_REDACTED_TEXT = "[Redacted]";
Redactor.KEY_MAX_LENGTH = 1024; // To mitigate ReDoS
Redactor.REDACTOR = new Redactor();
// =============================================================================
// Registered "global" redactors
// - Please note that each redactor adds to performance costs
// - Talk to a tech lead first before adding a "global" redactor here
// - Also consider only registering domain specific redactors in the relevant repos
// =============================================================================
Redactor.PASSWORD_REDACTOR = Redactor.registerRedactor(new Redactor([/password(s)?$/i]));
Redactor.SECRET_REDACTOR = Redactor.registerRedactor(new Redactor([/secret(s)?$/i]));
Redactor.CERT_REDACTOR = Redactor.registerRedactor(new Redactor([/cert(s)?$/i, /key(s)?$/i]));
Redactor.ADDRESS_REDACTOR = Redactor.registerRedactor(new Redactor([/address(es)?$/i, /postal(code)?(s)?$/i]));
Redactor.EMAIL_REDACTOR = Redactor.registerRedactor(new Redactor([/email(s)?$/i], (value) => {
    const [address, domain] = value.split("@");
    if (!address || !domain) {
        return Redactor.DEFAULT_REDACTED_TEXT;
    }
    if (address.length < 4) {
        return "*".repeat(address.length) + "@" + domain;
    }
    const redactedLen = Math.floor(address.length / 2);
    const frontPadLen = Math.floor((address.length - redactedLen) / 2);
    const backPadLen = address.length - redactedLen - frontPadLen;
    return address.slice(0, frontPadLen) + "*".repeat(redactedLen) + address.slice(-backPadLen) + "@" + domain;
}));
Redactor.CONTACT_REDACTOR = Redactor.registerRedactor(new Redactor([/mobileno(s)?$/i, /contactno(s)?$/i, /phoneno(s)?$/i], (value) => {
    if (value.length < 8) {
        return "*".repeat(value.length);
    }
    const redactedLen = Math.min(Math.floor(value.length / 2), 3);
    const backPadLen = 1;
    const frontPadLen = value.length - redactedLen - backPadLen;
    return value.slice(0, frontPadLen) + "*".repeat(redactedLen) + value.slice(-backPadLen);
}));
Redactor.UINFIN_REDACTOR = Redactor.registerRedactor(new Redactor([/uinfin(s)?$/i, /nric(s)?$/i], (value) => {
    if (value.length < 9) {
        return "*".repeat(value.length);
    }
    return value.slice(0, 1) + "****" + value.slice(-4);
}));
Redactor.SINGPASS_REDACTOR = Redactor.registerRedactor(new Redactor([/^singpassSessionId$/, /^PD-S-SESSION-ID$/]));
//# sourceMappingURL=Redactor.js.map